'use client';

import { c as _c } from "react/compiler-runtime";
import { FactiiiGoalType, FactiiiRequirement, type PostFactiii } from '@shared/all/domains/factiiis';
import { postDataSort } from '@shared/all/domains/posts';
import { type PostFeedInput } from '@shared/all/validators';
import getTimeBySort from '@shared/utils/helpers/getTimeBySort';
import groupByFactiiiId from '@shared/utils/helpers/groupByFactiiiId';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useUser from '@shared/utils/hooks/useUser';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, PlusCircle } from 'lucide-react';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import { NewPostModal } from './NewPost/NewPostModal';
type Props = {
  initialFactiiiData: PostFactiii[][];
  params: PostFeedInput;
};
const DataItem = props => {
  const $ = _c(88);
  const [expanded, setExpanded] = useState(false);
  const factiii = props.initialFactiiiData[0][0];
  const {
    queryKey
  } = usePostsShared(client);
  const {
    currentUser
  } = useUser();
  const {
    getFactiiiId
  } = useFactiiis(client);
  const {
    localPreferenceState,
    getPostParams
  } = useLocalPreference();
  const [newPostModalOpen, setNewPostModalOpen] = useState(false);
  const t0 = factiii.factiiiId;
  let t1;
  if ($[0] !== factiii.factiiiId || $[1] !== getPostParams || $[2] !== props.params) {
    t1 = getPostParams(props.params, undefined, [factiii.factiiiId]);
    $[0] = factiii.factiiiId;
    $[1] = getPostParams;
    $[2] = props.params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== factiii.factiiiId || $[5] !== queryKey || $[6] !== t1) {
    t2 = [queryKey, t0, t1];
    $[4] = factiii.factiiiId;
    $[5] = queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== factiii.factiiiId || $[9] !== getPostParams || $[10] !== props.params) {
    t3 = async t4 => {
      const {
        pageParam
      } = t4;
      const {
        posts
      } = await unwrapFromResult(client.posts.feed.query(getPostParams(props.params, pageParam, [factiii.factiiiId])));
      const hasMore = posts.length > 1;
      const nextTimestamp = hasMore ? new Date(posts[posts.length - 1].createdAt).getTime() : undefined;
      if (hasMore) {
        posts.pop();
      }
      return {
        posts,
        nextTimestamp
      };
    };
    $[8] = factiii.factiiiId;
    $[9] = getPostParams;
    $[10] = props.params;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = Date.now();
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = Boolean(localPreferenceState.postModeType) && Boolean(localPreferenceState.postFilterType) && currentUser.isAuthenticated !== null && expanded;
  let t6;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t5) {
    t6 = {
      queryKey: t2,
      queryFn: t3,
      initialPageParam: t4,
      getNextPageParam: _temp,
      enabled: t5,
      staleTime: 60000
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const {
    data,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t6);
  let t7;
  if ($[17] !== currentUser.isAuthenticated || $[18] !== factiii.factiiiId) {
    t7 = ["factiii", "data", factiii.factiiiId, currentUser.isAuthenticated];
    $[17] = currentUser.isAuthenticated;
    $[18] = factiii.factiiiId;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== currentUser.isAuthenticated || $[21] !== factiii.factiiiId || $[22] !== getFactiiiId) {
    t8 = async () => {
      const factiiiData = await getFactiiiId(factiii.factiiiId);
      if (factiiiData) {
        return {
          ...factiiiData,
          goals: currentUser.isAuthenticated ? (await unwrapFromResult(client.factiiis.factiiiGoals.query({
            factiiiId: factiii.factiiiId
          }))).goals : []
        };
      }
    };
    $[20] = currentUser.isAuthenticated;
    $[21] = factiii.factiiiId;
    $[22] = getFactiiiId;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const t9 = Boolean(factiii.factiiiId) && expanded;
  let t10;
  if ($[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9,
      staleTime: 60000
    };
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const {
    data: factiiiData_0
  } = useQuery(t10);
  const calculate = _temp2;
  let t11;
  if ($[28] !== data || $[29] !== factiii.factiiiId) {
    t11 = data ? groupByFactiiiId(data.pages).find(f => f[0][0].factiiiId === factiii.factiiiId) : undefined;
    $[28] = data;
    $[29] = factiii.factiiiId;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const items = t11;
  let t12;
  bb0: {
    if (!items) {
      t12 = 0;
      break bb0;
    }
    const calculateDaysBetween = _temp3;
    const lastBatch = items[items.length - 1];
    if (!lastBatch?.length) {
      t12 = 0;
      break bb0;
    }
    const lastPost = lastBatch[lastBatch.length - 1];
    if (!lastPost || !lastPost.postFactiiis?.length) {
      t12 = 0;
      break bb0;
    }
    let t13;
    if ($[31] !== lastPost.postFactiiis[0].createdAt) {
      t13 = calculateDaysBetween(new Date(lastPost.postFactiiis[0].createdAt || 0));
      $[31] = lastPost.postFactiiis[0].createdAt;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    t12 = t13;
  }
  const getNumberOfDays = t12;
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setExpanded(_temp4);
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== expanded) {
    t14 = <button className="mr-2 p-1 hover:text-primary" onClick={t13}>{expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</button>;
    $[34] = expanded;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = `/fids/${factiii.factiiiId}`;
  const t16 = factiiiData_0?.avatarKey ?? "";
  let t17;
  if ($[36] !== factiii.factiiiId || $[37] !== t16) {
    t17 = <Avatar size={35} factiiiAvatarKey={t16} factiiiId={factiii.factiiiId} />;
    $[36] = factiii.factiiiId;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t15 || $[40] !== t17) {
    t18 = <Link onClick={_temp5} href={t15}>{t17}</Link>;
    $[39] = t15;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = `/fids/${factiii.factiiiId}`;
  let t20;
  if ($[42] !== factiii.name) {
    t20 = <p className="font-semibold leading-none">{factiii.name}</p>;
    $[42] = factiii.name;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t19 || $[45] !== t20) {
    t21 = <div className="flex items-center"><Link onClick={_temp6} href={t19}>{t20}</Link></div>;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== factiiiData_0?.goals || $[48] !== localPreferenceState.postSortBy) {
    t22 = factiiiData_0?.goals && <div className="flex gap-x-2">{factiiiData_0?.goals.filter(goal => goal.type === FactiiiGoalType.DAILY && localPreferenceState.postSortBy === postDataSort.DAILY || goal.type === FactiiiGoalType.WEEKLY && localPreferenceState.postSortBy === postDataSort.WEEKLY || goal.type === FactiiiGoalType.MONTHLY && localPreferenceState.postSortBy === postDataSort.MONTHLY).map(_temp7)}</div>;
    $[47] = factiiiData_0?.goals;
    $[48] = localPreferenceState.postSortBy;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t22) {
    t23 = <div className="mt-1 flex items-center"><div className="text-sm text-textSecondary/70">{t22}</div></div>;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t21 || $[53] !== t23) {
    t24 = <div className="ml-4 flex flex-col">{t21}{t23}</div>;
    $[52] = t21;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t14 || $[56] !== t18 || $[57] !== t24) {
    t25 = <div className="flex items-center">{t14}{t18}{t24}</div>;
    $[55] = t14;
    $[56] = t18;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  const t26 = items?.length;
  let t27;
  if ($[59] !== t26) {
    t27 = <div className="text-sm text-textSecondary/70">Total: {t26}</div>;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <PlusCircle />;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Button titleElement={t28} variant="SECONDARY" borderTransparent={false} classNames="hover:bg-background-light w-auto" onClick={() => setNewPostModalOpen(true)} />;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t27) {
    t30 = <div className="flex items-end gap-x-4">{t27}{t29}</div>;
    $[63] = t27;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t25 || $[66] !== t30) {
    t31 = <div className="sticky top-[59px] mb-4 flex items-center justify-between rounded-md border-2 border-ds-border-standard bg-background-primary p-4">{t25}{t30}</div>;
    $[65] = t25;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== expanded || $[69] !== factiii.requirements || $[70] !== factiiiData_0?.goals || $[71] !== fetchNextPage || $[72] !== getNumberOfDays || $[73] !== hasNextPage || $[74] !== isFetching || $[75] !== isFetchingNextPage || $[76] !== items || $[77] !== localPreferenceState.postSortBy) {
    t32 = expanded && items && <div><div className="transition-all duration-300 ease-in-out">{items.map((group, i_0) => <div key={i_0} className="p-4"><h1 className="my-4 text-center font-semibold italic text-textSecondary/70">{(() => {
              const lastCreatedAt = group[group.length - 1]?.postFactiiis[group[group.length - 1].postFactiiis.length - 1].createdAt;
              const date = lastCreatedAt ? new Date(lastCreatedAt) : null;
              return date ? getTimeBySort(date, localPreferenceState.postSortBy) : null;
            })()}</h1><div className="overflow-hidden rounded-lg ring-2 ring-ds-border-standard"><table className="w-full border-collapse"><thead><tr><th className="border border-ds-border-standard p-2">User</th><th className="border border-ds-border-standard p-2">Time</th><th className="border border-ds-border-standard p-2">Count</th>{factiii.requirements.includes(FactiiiRequirement.INTEGER_REQUIRED) && <th className="border border-ds-border-standard p-2">Remaining</th>}</tr></thead><tbody>{group.flatMap((row, rowIndex) => row.postFactiiis.map((postFactiii, pfIndex) => {
                  const postUser = row.user;
                  return <tr key={`${rowIndex}-${pfIndex}`}><td className="border border-ds-border-standard p-2 text-center"><Link href={`/user/${postUser.username}`}><div className="flex items-center space-x-2"><Avatar size={32} avatarKey={postUser.avatarKey ?? null} robohash={postUser.robohash ?? ""} isBot={postUser.tag === "BOT"} userId={postUser.id} /><div className="text-sm font-emphasis">@{postUser.username}</div></div></Link></td><td className="border border-ds-border-standard p-2 text-center">{postFactiii.createdAt ? new Date(postFactiii.createdAt).toLocaleString("en-US", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true
                      }) : "unknown"}</td><td className="border border-ds-border-standard p-2 text-center">{postFactiii.number ?? postFactiii.data}</td>{row.requirements.includes(FactiiiRequirement.INTEGER_REQUIRED) && <td className="border border-ds-border-standard p-2 text-center">{factiiiData_0?.goals?.filter(goal_0 => goal_0.type === FactiiiGoalType.DAILY && localPreferenceState.postSortBy === postDataSort.DAILY || goal_0.type === FactiiiGoalType.WEEKLY && localPreferenceState.postSortBy === postDataSort.WEEKLY || goal_0.type === FactiiiGoalType.MONTHLY && localPreferenceState.postSortBy === postDataSort.MONTHLY).map(filteredGoal_0 => <div key={filteredGoal_0.id}>{Math.max(0, Number(filteredGoal_0.data) - calculate(group.flat(), rowIndex))}</div>)}</td>}</tr>;
                }))}</tbody></table></div></div>)}</div><div className="flex items-center gap-x-2 p-4 text-sm"><div className="flex space-x-1"><p className="font-semibold">{getNumberOfDays}</p><p>{getNumberOfDays > 1 ? "days" : "day"} of posts loaded</p></div>{hasNextPage && <>•<Button text={isFetchingNextPage ? "" : "Load More"} variant="SECONDARY" loading={isFetchingNextPage} onClick={() => void fetchNextPage()} disabled={!hasNextPage || isFetchingNextPage || isFetching} classNames="!p-0 hover:bg-transparent !text-primary hover:underline" /></>}</div></div>;
    $[68] = expanded;
    $[69] = factiii.requirements;
    $[70] = factiiiData_0?.goals;
    $[71] = fetchNextPage;
    $[72] = getNumberOfDays;
    $[73] = hasNextPage;
    $[74] = isFetching;
    $[75] = isFetchingNextPage;
    $[76] = items;
    $[77] = localPreferenceState.postSortBy;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== currentUser.isAuthenticated || $[80] !== factiiiData_0 || $[81] !== newPostModalOpen) {
    t33 = currentUser.isAuthenticated && newPostModalOpen && <NewPostModal closeHandler={() => setNewPostModalOpen(false)} pinnedFactiii={factiiiData_0} />;
    $[79] = currentUser.isAuthenticated;
    $[80] = factiiiData_0;
    $[81] = newPostModalOpen;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== factiii.factiiiId || $[84] !== t31 || $[85] !== t32 || $[86] !== t33) {
    t34 = <div key={factiii.factiiiId} className="rounded-md bg-background-secondary shadow-lg">{t31}{t32}{t33}</div>;
    $[83] = factiii.factiiiId;
    $[84] = t31;
    $[85] = t32;
    $[86] = t33;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  return t34;
};
export default DataItem;
function _temp(lastPage) {
  const posts_0 = lastPage?.posts ?? [];
  if (posts_0.length === 0) {
    return;
  }
  return lastPage.nextTimestamp;
}
function _temp2(dataFactiii, index) {
  let sum = 0;
  for (let i = dataFactiii.length - 1; i >= index; i--) {
    const factiii_0 = dataFactiii[i];
    if (factiii_0?.postFactiiis) {
      for (const pf of factiii_0.postFactiiis) {
        sum = sum + (pf.number ?? 0);
      }
    }
  }
  return sum;
}
function _temp3(startDate) {
  const currentDate = new Date();
  const start = new Date(startDate);
  const differenceInTime = currentDate.getTime() - start.getTime();
  const differenceInDays = Math.ceil(differenceInTime / 86400000);
  return differenceInDays;
}
function _temp4(prev) {
  return !prev;
}
function _temp5(e) {
  return e.stopPropagation();
}
function _temp6(e_0) {
  return e_0.stopPropagation();
}
function _temp7(filteredGoal) {
  return <div key={filteredGoal.id}>Goal: {filteredGoal.data}</div>;
}